import React from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { Spin, TimePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const DietPlanEditModal = ({
  isOpen,
  onClose,
  editPatient,
  patientFormData,
  handleInputChange,
  handleMealChange,
  addMeal,
  removeMeal,
  handleDayFieldChange,
  handleUpdatePatient,
  loading
}) => {
  if (!isOpen || !editPatient) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Edit Patient Diet Plan</h2>
            <p className="text-sm text-gray-500">
              Patient: <span className="font-semibold text-[#159da8]">{patientFormData.patientName}</span>
            </p>
          </div>
          <label onClick={onClose} className="text-2xl text-gray-500 hover:text-red-500 cursor-pointer p-2">×</label>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 scrollwidth">
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Diet Title</label>
                <input
                  name="dietTitle"
                  value={patientFormData.dietTitle}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#159da8] transition-all"
                  placeholder="e.g., Weight Loss Plan"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">From Date</label>
                <input
                  type="date"
                  name="fromDate"
                  value={patientFormData.fromDate}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#159da8]"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">To Date</label>
                <input
                  type="date"
                  name="toDate"
                  value={patientFormData.toDate}
                  disabled
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                />
              </div>
              {/* <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Status</label>
                <select
                  name="status"
                  value={patientFormData.status}
                  onChange={handleInputChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#159da8]"
                >
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                </select>
              </div> */}
            </div>

            <div className="space-y-6">
              {Object.entries(
                (editPatient.patientDietPlanDetails || [])
                  .sort((a, b) => {
                    const dayA = parseInt(a.dayTitle.match(/\d+/)?.[0] || 0);
                    const dayB = parseInt(b.dayTitle.match(/\d+/)?.[0] || 0);
                    if (dayA !== dayB) return dayA - dayB;
                    return (a.sortOrder || 0) - (b.sortOrder || 0);
                  })
                  .reduce((acc, curr) => {
                    if (!acc[curr.dayTitle]) acc[curr.dayTitle] = [];
                    acc[curr.dayTitle].push(curr);
                    return acc;
                  }, {})
              ).map(([dayTitle, meals]) => (
                <div key={dayTitle} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 bg-emerald-50/50 border-b border-emerald-100 flex justify-between items-center">
                    <h3 className="font-bold text-emerald-800 flex items-center gap-2">
                      <span className="w-8 h-8 bg-emerald-600 text-white rounded-lg flex items-center justify-center text-sm">
                        {dayTitle.match(/\d+/)?.[0]}
                      </span>
                      {dayTitle}
                    </h3>

                    <div className="mt-2 flex justify-start">
                      <button
                        onClick={() => addMeal(dayTitle)}
                        className="text-xs bg-emerald-50 text-emerald-600 border border-emerald-200 px-4 py-2 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1.5 font-semibold"
                      >
                        <FaPlus size={10} /> Add Another Meal
                      </button>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-gray-400 uppercase text-[10px] font-bold tracking-wider">
                          <tr>
                            <th className="px-4 py-2 text-left w-32">Time</th>
                            <th className="px-4 py-2 text-left w-40">Type</th>
                            <th className="px-4 py-2 text-left">Meal Description</th>
                            <th className="px-4 py-2 text-center w-16"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {meals.map((meal, idx) => (
                            <tr key={idx} className="group">
                              <td className="px-4 py-3">
                                <TimePicker
                                  use12Hours
                                  format="h:mm A"
                                  value={meal.mealTime ? dayjs(meal.mealTime, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null}
                                  onChange={(time) => handleMealChange(dayTitle, idx, "mealTime", time ? time.format("HH:mm:ss") : "")}
                                  className="w-full bg-gray-50 border-transparent focus:bg-white focus:border-emerald-200 rounded-lg px-2 py-1.5 text-sm outline-none transition-all"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={meal.mealType}
                                  onChange={(e) => handleMealChange(dayTitle, idx, "mealType", e.target.value)}
                                  className="w-full bg-gray-50 border-transparent focus:bg-white focus:border-emerald-200 rounded-lg px-2 py-1.5 text-sm outline-none transition-all"
                                >
                                  <option value="EARLY MORNING">Early Morning</option>
                                  <option value="BREAKFAST">Breakfast</option>
                                  <option value="MID-MORNING">Mid-Morning</option>
                                  <option value="LUNCH">Lunch</option>
                                  <option value="EVENING SNACK">Evening Snack</option>
                                  <option value="DINNER">Dinner</option>
                                  <option value="BEDTIME">Bedtime</option>
                                </select>
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  value={meal.meal}
                                  onChange={(e) => handleMealChange(dayTitle, idx, "meal", e.target.value)}
                                  placeholder="Enter meal details..."
                                  className="w-full bg-gray-50 border-transparent focus:bg-white focus:border-emerald-200 rounded-lg px-3 py-1.5 text-sm outline-none transition-all"
                                />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => removeMeal(dayTitle, idx)}
                                  className="text-gray-300 hover:text-red-500 transition-colors p-1"
                                >
                                  <FaTimes size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-50 pt-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Activity</label>
                        <textarea
                          value={meals[0]?.activity || ""}
                          onChange={(e) => handleDayFieldChange(dayTitle, "activity", e.target.value)}
                          rows="1"
                          placeholder="Add physical activity for the day..."
                          className="w-full bg-amber-50/30 border-transparent focus:bg-white focus:border-amber-200 rounded-xl px-4 py-2 text-sm outline-none transition-all resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">Notes</label>
                        <textarea
                          value={meals[0]?.notes || ""}
                          onChange={(e) => handleDayFieldChange(dayTitle, "notes", e.target.value)}
                          rows="1"
                          placeholder="Add additional notes..."
                          className="w-full bg-blue-50/30 border-transparent focus:bg-white focus:border-blue-200 rounded-xl px-4 py-2 text-sm outline-none transition-all resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t px-8 py-5 bg-white flex justify-end gap-4 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
          <button onClick={onClose} className="px-6 py-2.5 text-sm font-semibold text-black bg-white border border-gray-200 rounded-xl transition-colors hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleUpdatePatient}
            disabled={loading}
            className="px-8 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 rounded-xl shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
          >
            {loading ? <><Spin size="small" /> Updating...</> : "Update Plan"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DietPlanEditModal;
